import type { ReactNode } from "react";
import { formatPrice } from "@/lib/currency";
import {
  type CheckoutDefaults,
  DEFAULT_CHECKOUT_DEFAULTS,
  type PricingPolicy,
  type PricingSnapshot,
  resolveCheckoutPolicy,
} from "@/lib/pricing/types";
import { PlatformFeeInfo } from "./platform-fee-info.client";

export function PricingDisclosure({
  policy,
  defaults = DEFAULT_CHECKOUT_DEFAULTS,
}: {
  policy?: PricingPolicy | null;
  /** The hiive's checkout defaults, for a policy field that inherits. */
  defaults?: CheckoutDefaults;
}) {
  if (!policy) return null;
  if (
    defaults === DEFAULT_CHECKOUT_DEFAULTS &&
    (policy?.taxDisplayMode === "inherit" ||
      policy?.feeFundingMode === "inherit")
  ) {
    return (
      <span className="text-xs text-text-tertiary">
        Final total shown at checkout
      </span>
    );
  }
  // Resolve "inherit" away first: a raw "inherit" is not a display mode, and
  // rendering the list from it produced a bare "+  at checkout".
  const resolved = resolveCheckoutPolicy(policy, defaults);
  const extras = [
    policy?.discoveryTaxMode === "excluded" ||
    (!policy?.discoveryTaxMode && resolved.taxDisplayMode === "add_at_checkout")
      ? "tax"
      : null,
    resolved.feeFundingMode === "buyer" && !policy?.discoveryIncludePlatformFee
      ? "platform fee"
      : null,
  ].filter(Boolean);
  if (extras.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-text-tertiary">
      + {extras.join(" and ")} at checkout
      {resolved.feeFundingMode === "buyer" ? <PlatformFeeInfo /> : null}
    </span>
  );
}

export function PricingBreakdown({
  snapshot,
  showTotal = true,
}: {
  snapshot: PricingSnapshot;
  showTotal?: boolean;
}) {
  const rows = new Map<string, { label: string; amount: number }>();
  for (const line of snapshot.lines)
    for (const component of line.taxSnapshot.lines) {
      if (component.amountMinor <= 0) continue;
      const included = component.includedInPrice && line.priceIncludesTax;
      const label = `${component.label}${included ? " (included)" : ""}`;
      const key = `${component.code}:${component.rateBps}:${included}`;
      const previous = rows.get(key);
      rows.set(key, {
        label,
        amount: (previous?.amount ?? 0) + component.amountMinor,
      });
    }
  const money = (amount: number) => formatPrice(amount, snapshot.currency);
  return (
    <dl className="space-y-2 text-sm tabular-nums">
      {snapshot.lines.map((line) => (
        <PriceRow
          key={`supply:${line.id}`}
          label={line.label ?? (line.id === "delivery" ? "Delivery" : "Item")}
          amount={money(line.amountMinor * line.quantity)}
        />
      ))}
      <PriceRow
        label="Advertised subtotal"
        amount={money(snapshot.displaySubtotalMinor)}
      />
      {snapshot.discountMinor > 0 ? (
        <PriceRow
          label="Discount"
          amount={`−${money(snapshot.discountMinor)}`}
        />
      ) : null}
      {[...rows.entries()].map(([key, row]) => (
        <PriceRow key={key} label={row.label} amount={money(row.amount)} />
      ))}
      {snapshot.buyerFeeMinor > 0 ? (
        <PriceRow
          label={
            <PlatformFeeLabel includesGst={snapshot.buyerFeeTaxMinor > 0} />
          }
          amount={money(snapshot.buyerFeeMinor)}
        />
      ) : null}
      {showTotal ? (
        <PriceRow label="Customer total" amount={money(snapshot.totalMinor)} />
      ) : null}
    </dl>
  );
}
function PlatformFeeLabel({ includesGst }: { includesGst: boolean }) {
  return (
    <span className="inline-flex items-center gap-1">
      Platform fee <PlatformFeeInfo includesGst={includesGst} />
    </span>
  );
}

function PriceRow({ label, amount }: { label: ReactNode; amount: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-text-secondary">{label}</dt>
      <dd>{amount}</dd>
    </div>
  );
}
